// NO "use client" — same reasoning as sections.tsx: this is the panel body
// apps/web renders from a SERVER component so the legal text is present in the
// server-rendered HTML. Client hosts (the legal-shell carousel's off-screen
// neighbour panels, the legal modal, apps/app) render the exact same component
// so the markup is byte-identical on both sides and swapping between them
// during a swipe is invisible.

import { DpaBlock, type LegalInfo } from "./dpa-block";
import { renderLegalSection, type LegalSectionOptions, type LegalTranslator } from "./sections";
import { LEGAL_LAST_UPDATED, type LegalSegment } from "./segments";

/**
 * Typography wrapper shared by every legal panel. Exported so hosts that need
 * to wrap their own content in the same rhythm (e.g. the standalone
 * report-content page) stay visually consistent.
 */
// Kept as ONE literal: Tailwind's source scanner reads raw text, and a single
// unbroken string guarantees every candidate class is emitted into the CSS.
// apps/web scans this package via `@source` in globals.css.
export const LEGAL_PROSE_CLASS =
  "prose prose-sm dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground max-w-none";

/**
 * DPO mailbox printed in the panel footer ("Questions about your data?").
 * Same address as the GDPR page's DPO block and the Contact page.
 */
export const LEGAL_CONTACT_EMAIL = "dpo@pontimed.app";

export interface LegalPanelProps extends LegalSectionOptions {
  /** Which legal document to render. */
  segment: LegalSegment;
  /** Translator bound to the active locale — `getLegalT(locale)`. */
  t: LegalTranslator;
  /** Active locale, for resolving the LocalizedString fields on `legalInfo`. */
  locale: string;
  /** Per-country DPA / regulatory block. Omit to hide it. */
  legalInfo?: LegalInfo | null;
  /** Country display name in the active locale (heading of the DPA block). */
  countryName?: string;
  /** Extra classes on the prose wrapper (spacing differs per host). */
  className?: string;
}

/**
 * One legal document: the section itself, the per-country DPA block, and the
 * version/contact footer. This is the whole indexable body of a `/legal/*`
 * page — keep it free of hooks and browser APIs so it renders on the server.
 */
export function LegalPanel({
  segment,
  t,
  locale,
  legalInfo = null,
  countryName = "",
  className = "pb-12",
  titleAs,
  titleClassName,
  links,
}: LegalPanelProps) {
  return (
    <div className={`${LEGAL_PROSE_CLASS} ${className}`}>
      {renderLegalSection(segment, t, { titleAs, titleClassName, links })}

      {/* Country-specific block (same for every section, lives inside the
          panel so it scrolls with the section content). */}
      {legalInfo && (
        <DpaBlock t={t} legalInfo={legalInfo} countryName={countryName} locale={locale} />
      )}

      <div className="border-border mt-12 border-t pt-6">
        <p className="text-muted-foreground text-xs">
          {t("lastUpdated", { date: LEGAL_LAST_UPDATED })}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          {t("contactDpo")}{" "}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-primary hover:underline">
            {LEGAL_CONTACT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Placeholder for a panel whose locale catalog hasn't landed yet. Only ever
 * shown for the carousel's off-screen NEIGHBOUR panels on non-bundled locales
 * (everything but EN/RO) — the active panel is always server-rendered text, so
 * this never stands in for indexable content.
 */
export function LegalPanelSkeleton({ className = "pb-12" }: { className?: string }) {
  return (
    <div className={className} aria-hidden="true">
      <div className="animate-pulse space-y-4">
        <div className="bg-muted h-7 w-2/3 rounded-lg" />
        <div className="bg-muted h-4 w-full rounded" />
        <div className="bg-muted h-4 w-11/12 rounded" />
        <div className="bg-muted h-4 w-4/5 rounded" />
        <div className="bg-muted mt-8 h-5 w-1/3 rounded" />
        <div className="bg-muted h-4 w-full rounded" />
        <div className="bg-muted h-4 w-10/12 rounded" />
        <div className="bg-muted h-4 w-9/12 rounded" />
      </div>
    </div>
  );
}
